import * as React from 'react'
import { supabase } from './supabase'
import { londonDayBounds, londonToday } from './dateSettingsCalendar'

type State = { day: string; count: number | null; unavailable: boolean }

export function TodaysBookingsMetric({ active, refreshKey }: { active: boolean; refreshKey: number }) {
  const [state, setState] = React.useState<State>({ day: '', count: null, unavailable: false })

  React.useEffect(() => {
    if (!active) return
    let disposed = false
    let pending = false
    const controller = new AbortController()
    const refresh = async () => {
      if (document.visibilityState !== 'visible' || pending) return
      pending = true
      const day = londonToday()
      const { start, end } = londonDayBounds(day)
      setState(previous => previous.day === day ? previous : { day, count: null, unavailable: false })
      try {
        // The shared overview includes online and manual bookings, excluding duplicate imports.
        // Unassigned online bookings have completed payment and only await equipment allocation.
        // Count confirmed bookings, not incomplete checkouts, tentative hires or cancelled records.
        const { count, error } = await supabase.from('ops_booking_overview')
          .select('id', { count: 'exact', head: true })
          .in('lifecycle_status', ['confirmed', 'unassigned'])
          .gte('created_at', start).lt('created_at', end)
          .abortSignal(controller.signal)
        if (error || count === null) throw new Error('Booking count unavailable')
        if (!disposed) setState({ day, count, unavailable: false })
      } catch {
        if (!disposed) setState({ day, count: null, unavailable: true })
      } finally {
        pending = false
      }
    }
    void refresh()
    const interval = setInterval(() => void refresh(), 60_000)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      disposed = true
      controller.abort()
      clearInterval(interval)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [active, refreshKey])

  return <article className="metric">
    <small>Today's bookings</small>
    <strong>{state.count === null ? '—' : state.count.toLocaleString('en-GB')}</strong>
    {state.unavailable && <span className="metric-note" role="status">Booking count unavailable</span>}
  </article>
}
